import pixUtils from '@/services/common/pix.utils.js';
import math from './pixMath.utils';
import Console from '@/shared/console';
import regexConst from '@/consts/regex.const.js';
import pixEvaluateUtils from './pixEvaluate.utils.js';
import memoize from 'lodash.memoize';
import pixRegexUtils from '../regularExpressions/pixRegex.utils.js';
import cssParseUtils from './cssParse.utils';

const hardcodedComputedOperationEquivalences = new Map([['[CURRENT_YEAR]', new Date().getFullYear()]]);

const getReplacedHardcodedComputedOperationStrings = function (computedOperation) {
  let ret = computedOperation;
  for (const [key, value] of hardcodedComputedOperationEquivalences) {
    ret = ret.replace(key, value);
  }

  return ret;
};

const mathEvaluation = memoize(
  function (operation, field) {
    let mathEval = null;
    try {
      mathEval = pixEvaluateUtils.pixEvaluate(operation);
      Console.groupCollapsed(`🗸 - Evaluate mathematical expression for field - ${field}`);
      Console.log(`Computed field calculated correctly with value \n${mathEval}`);
    } catch (err) {
      Console.groupCollapsed(`✕ - Evaluate mathematical expression for field - ${field}`);
      Console.error(`Invalid mathematical evaluation \n${operation}`);
      Console.error(err);
      throw `${operation}`;
    } finally {
      Console.groupEnd();
    }

    return mathEval;
  },
  (...args) =>
    Object.entries(args)
      .map(([_key, value]) => value)
      .join('_')
);

/* This function returns the value of a field in a form
But instead of going directly to the model, first it tries
to get the value straight from the HTML input which might be updated
*/
const getValueForField = (fieldId, formId, itemModel, nodeElementToFind = document) => {
  const getValueAfterSaveTransform = (inputNode) => {
    const saveTransform = inputNode?.parentElement?.dataset?.saveTransform;
    let value = inputNode?.value;

    if (saveTransform && saveTransform !== 'null' && value) {
      value = math.evaluate(saveTransform.replace('this', value));
    }

    return value;
  };

  const getValueFromOriginalValue = (inputNode) => {
    return inputNode?.dataset?.originalValue;
  };

  // Get the value directly from the model
  const getFieldValueInModel = (fieldId, itemModel) => {
    return itemModel.find((a) => a.InternalField === fieldId)?.Value;
  };

  const getNodeThatBelongToThisFormId = (fieldId, formId) => {
    return nodeElementToFind.querySelector(`#${cssParseUtils.saniticeCssSelector(fieldId)}-${formId} input`);
  };

  const getNodeThatBelongToOtherFormId = (fieldId) => {
    // Take into account we are taking the FIRST element that matches, and querySelector could match many
    return nodeElementToFind.querySelector(`[id^=${cssParseUtils.saniticeCssSelector(fieldId)}-] input`);
  };

  const checkValid = (fieldValue) => {
    if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
      return fieldValue;
    } else {
      return null;
    }
  };

  const nodeInThisFormId = getNodeThatBelongToThisFormId(fieldId, formId);
  const nodeInOtherFormId = getNodeThatBelongToOtherFormId(fieldId);
  let valueFromRenderedForm;

  if (nodeInThisFormId) {
    valueFromRenderedForm = checkValid(getValueAfterSaveTransform(nodeInThisFormId)) ?? checkValid(getValueFromOriginalValue(nodeInThisFormId));
  } else if (nodeInOtherFormId) {
    valueFromRenderedForm = checkValid(getValueAfterSaveTransform(nodeInOtherFormId)) ?? checkValid(getValueFromOriginalValue(nodeInOtherFormId));
  }

  return valueFromRenderedForm ?? checkValid(getFieldValueInModel(fieldId, itemModel)) ?? null;
};

// Función para obtener el valor computado de un campo, dentro de una tabla genérica, accediendo tanto al modelo de la fila como al modelo del formulario
const getComputedValueFromTableAndModel = (computedExpression, row, model, propertyToEvaluate) => {
  const regexMatches = pixRegexUtils.getRegexMatches(regexConst.WHOLE_WORD_FILTERS, computedExpression);

  let modifiedExpression = computedExpression;

  // Searches into the row
  for (const match of regexMatches) {
    if (row[match]) {
      modifiedExpression = modifiedExpression.replace(match, row[match]);
    }
  }

  // Searches into the model
  for (const match of regexMatches) {
    const propertyToEvaluateValue = model.find((el) => el.InternalField === match)?.[propertyToEvaluate];

    if (!propertyToEvaluateValue) return;

    modifiedExpression = modifiedExpression.replace(match, propertyToEvaluateValue);
  }

  return mathEvaluation(modifiedExpression, computedExpression);
};

// Función para obtener el valor computado de este campo, accediendo al modelo unicamente
const getValueFromComputedOperation = (model, metricKey, currentLang, computedOperationParam) => {
  if (computedOperationParam !== null && computedOperationParam !== undefined) {
    const computedOperation = getReplacedHardcodedComputedOperationStrings(computedOperationParam);
    const regexMatches = pixRegexUtils.getRegexMatches(regexConst.WHOLE_WORD, computedOperation);

    let replacedInternalFields = computedOperation;

    regexMatches.forEach(function (internalField) {
      /* The regexConst can match null and other words that are not internal fields */
      if (internalField !== 'null') {
        const relatedFieldValue = pixUtils.getFieldValue(internalField, model, null, null, currentLang, null);
        if (relatedFieldValue !== null && relatedFieldValue !== undefined && relatedFieldValue !== '') {
          // The quotes interpolation is necessary for comparing strings.
          replacedInternalFields = replacedInternalFields.replace(new RegExp(String.raw`${internalField}\b`, 'g'), typeof relatedFieldValue == 'string' ? `'${relatedFieldValue}'` : relatedFieldValue);
        }
      }
    });
    return mathEvaluation(replacedInternalFields, metricKey);
  }
};

// Función para obtener el valor computado de este campo, accediendo al valor de los inputs en html no solo al modelo
const getValueForThisComputedField = (computedExpression, regexMatches, computedField, formId, itemModel, nodeElementToFind) => {
  let replacedField = getReplacedHardcodedComputedOperationStrings(computedExpression);
  regexMatches.forEach((reliantFieldId) => {
    const reliantFieldValue = getValueForField(reliantFieldId, formId, itemModel, nodeElementToFind);

    if (reliantFieldValue) {
      /* si es válido, hacemos replace en la expresión matemática y continuamos bucle */
      replacedField = replacedField.replace(new RegExp(String.raw`${reliantFieldId}\b`, 'g'), reliantFieldValue);
    } else {
      /* Si uno de los campos no tiene valor, lo sustituimos por null */
      replacedField = replacedField.replace(new RegExp(String.raw`${reliantFieldId}\b`, 'g'), 'null');
    }
  });

  return mathEvaluation(replacedField, computedField);
};

export default {
  getReplacedHardcodedComputedOperationStrings,
  getComputedValueFromTableAndModel,
  getValueFromComputedOperation,
  getValueForThisComputedField,
  mathEvaluation,
};
